import {
  type JsonValue,
  type ResolutionDetails,
  StandardResolutionReasons,
} from '@openfeature/web-sdk'
import { isPlainObject } from 'lodash-es'

/**
 * How a query string value becomes a flag value. The browser's QueryProvider
 * and the server side evaluation both resolve through these, so a forced
 * variant lands on the same value whichever side answers.
 *
 * Coercion is driven by the type the caller asked for, not by the type the URL
 * happened to parse as, which is why each flag kind gets its own function.
 */

/** JSON when the value parses as JSON, otherwise the raw string. */
export function parseQueryValue(value: string): JsonValue {
  try {
    const parsed: JsonValue = JSON.parse(value)
    return parsed
  } catch {
    return value
  }
}

const matched = <T>(value: T): ResolutionDetails<T> => ({
  value,
  reason: StandardResolutionReasons.TARGETING_MATCH,
})

const fallback = <T>(value: T): ResolutionDetails<T> => ({
  value,
  reason: StandardResolutionReasons.DEFAULT,
})

export function coerceBoolean(
  value: JsonValue | null,
  defaultValue: boolean
): ResolutionDetails<boolean> {
  if (value === null) return fallback(defaultValue)
  if (
    value === 'true' ||
    value === true ||
    value === '1' ||
    value === 1 ||
    value === 'yes' ||
    value === 'on'
  ) {
    return matched(true)
  }
  if (
    value === 'false' ||
    value === '0' ||
    value === 0 ||
    value === 'no' ||
    value === 'off' ||
    value === undefined ||
    value === '' ||
    Number.isNaN(value) ||
    value === false
  ) {
    return matched(false)
  }
  return fallback(defaultValue)
}

export function coerceString(
  value: JsonValue | null,
  defaultValue: string
): ResolutionDetails<string> {
  if (value === null) return fallback(defaultValue)
  if (typeof value === 'string') return matched(value)
  if (typeof value === 'number') return matched(`${value}`)
  if (typeof value === 'boolean') return matched(`${value}`)
  // String typed flags can carry JSON (tabBadges is the real case). The parse
  // above is eager, so an array or object lands here and is re serialised, and
  // the caller still receives a string it can parse itself.
  if (typeof value === 'object') return matched(JSON.stringify(value))
  return fallback(defaultValue)
}

export function coerceNumber(
  value: JsonValue | null,
  defaultValue: number
): ResolutionDetails<number> {
  if (value === null) return fallback(defaultValue)
  if (typeof value === 'number') return matched(value)
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    if (!isNaN(parsed)) return matched(parsed)
  }
  return fallback(defaultValue)
}

export function coerceObject<T extends JsonValue>(
  value: JsonValue | null,
  defaultValue: T
): ResolutionDetails<T> {
  if (value === null) return fallback(defaultValue)
  if (isPlainObject(value) || Array.isArray(value)) {
    // The payload came from the query string this flag was asked for, so its
    // shape is the caller's own T.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return matched(value as T)
  }
  return fallback(defaultValue)
}
